import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const REQ_CATS = [["weekday", "Weekday"], ["weekend", "Weekend"], ["holiday", "Holiday"]];

export default function Sites() {
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [sel, setSel] = useState(null);
  const [posts, setPosts] = useState([]);
  const [acks, setAcks] = useState([]);
  const [poText, setPoText] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", branch_id: "", name: "", address: "", lat: "", lng: "", geofence_radius_m: 200 });
  const [postOpen, setPostOpen] = useState(false);
  const [postForm, setPostForm] = useState({ name: "", requirements: { weekday: { day: 1, night: 1 }, weekend: { day: 1, night: 1 }, holiday: { day: 1, night: 1 } } });

  const load = () => api.get("/sites").then((r) => setSites(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const pick = async (s) => {
    setSel(s);
    setPoText(s.post_orders || "");
    const [p, a] = await Promise.all([
      api.get(`/sites/${s.id}/posts`),
      api.get(`/sites/${s.id}/post-order-acks`),
    ]);
    setPosts(p.data);
    setAcks(a.data);
  };

  const createSite = async () => {
    try {
      await api.post("/sites", { ...form, lat: parseFloat(form.lat) || null, lng: parseFloat(form.lng) || null, geofence_radius_m: parseInt(form.geofence_radius_m) || 200 });
      toast.success("Site created");
      setNewOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const savePostOrders = async () => {
    try {
      const { data } = await api.put(`/sites/${sel.id}/post-orders`, { text: poText });
      toast.success(`Post orders saved (v${data.version})`);
      pick({ ...sel });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const createPost = async () => {
    try {
      await api.post(`/sites/${sel.id}/posts`, postForm);
      toast.success("Post created");
      setPostOpen(false);
      pick(sel);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setReq = (cat, shift, val) => {
    setPostForm({ ...postForm, requirements: { ...postForm.requirements, [cat]: { ...postForm.requirements[cat], [shift]: parseInt(val) || 0 } } });
  };

  return (
    <div data-testid="sites-page">
      <PageHeader title="Sites & Posts" sub="Client sites with GPS geofencing, posts and digital post orders"
        actions={<Button data-testid="new-site-button" className="rounded-none" onClick={() => setNewOpen(true)}>New site</Button>} />
      <DataTable testid="sites-table" onRowClick={pick} rows={sites}
        columns={[
          { key: "ref_no", label: "Ref", render: (r) => <span className="font-mono text-xs">{r.ref_no}</span> },
          { key: "name", label: "Site", render: (r) => <div><div className="font-semibold">{r.name}</div><div className="text-xs text-muted-foreground">{r.address}</div></div> },
          { key: "client_name", label: "Client" },
          { key: "branch_name", label: "Branch" },
          { key: "gps", label: "GPS / Geofence", render: (r) => <span className="font-mono text-xs">{r.lat ?? "—"}, {r.lng ?? "—"} · {r.geofence_radius_m}m</span> },
          { key: "po", label: "Post orders", render: (r) => <span className="font-mono text-xs">v{r.post_orders_version}</span> },
        ]} />

      <Dialog open={!!sel} onOpenChange={() => setSel(null)}>
        <DialogContent className="rounded-none max-w-3xl max-h-[85vh] overflow-auto" data-testid="site-detail-dialog">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{sel.name} <span className="font-mono text-xs text-muted-foreground">{sel.ref_no}</span></DialogTitle></DialogHeader>
              <div className="grid md:grid-cols-3 gap-3 text-sm mb-4">
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Client</div>{sel.client_name}</div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Branch</div>{sel.branch_name}</div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">QR code</div><span className="font-mono text-xs" data-testid="site-qr-code">{sel.qr_code}</span></div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">GPS</div><span className="font-mono text-xs">{sel.lat}, {sel.lng}</span></div>
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Geofence radius</div><span className="font-mono text-xs">{sel.geofence_radius_m}m</span></div>
              </div>

              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading font-bold">Posts & required headcount</h3>
                <Button size="sm" variant="outline" className="rounded-none" data-testid="new-post-button" onClick={() => setPostOpen(true)}>Add post</Button>
              </div>
              <DataTable testid="posts-table" rows={posts} empty="No posts yet"
                columns={[
                  { key: "name", label: "Post", render: (r) => <span className="font-semibold">{r.name}</span> },
                  ...REQ_CATS.map(([k, label]) => ({
                    key: k, label,
                    render: (r) => <span className="font-mono text-xs">D:{r.requirements?.[k]?.day ?? 0} N:{r.requirements?.[k]?.night ?? 0}</span>,
                  })),
                  { key: "active", label: "Status", render: (r) => <StatusBadge value={r.active ? "active" : "closed"} /> },
                ]} />

              <h3 className="font-heading font-bold mt-5 mb-2">Digital post orders <span className="font-mono text-xs text-muted-foreground">v{sel.post_orders_version}</span></h3>
              <Textarea data-testid="post-orders-text" className="rounded-none font-mono text-xs min-h-[140px]" value={poText} onChange={(e) => setPoText(e.target.value)} />
              <Button size="sm" className="rounded-none mt-2" data-testid="save-post-orders" onClick={savePostOrders}>Save new version</Button>

              <h3 className="font-heading font-bold mt-5 mb-2">Acknowledgements</h3>
              {acks.length === 0 && <div className="font-mono text-xs text-muted-foreground">No acknowledgements yet</div>}
              {acks.map((a, i) => (
                <div key={i} className="font-mono text-xs border-t border-border py-1">v{a.version} · {a.acknowledged_by} · {a.acknowledged_at?.slice(0, 16).replace("T", " ")}</div>
              ))}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none" data-testid="new-site-dialog">
          <DialogHeader><DialogTitle className="font-heading">New site</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="site-client" className="rounded-none mt-1"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Branch</Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="site-branch" className="rounded-none mt-1"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs uppercase tracking-wider font-bold">Site name</Label><Input data-testid="site-name" className="rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="col-span-2"><Label className="text-xs uppercase tracking-wider font-bold">Address</Label><Input data-testid="site-address" className="rounded-none mt-1" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Latitude</Label><Input data-testid="site-lat" className="rounded-none mt-1" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="14.4246" /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Longitude</Label><Input data-testid="site-lng" className="rounded-none mt-1" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="121.0297" /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">Geofence radius (m)</Label><Input data-testid="site-radius" type="number" className="rounded-none mt-1" value={form.geofence_radius_m} onChange={(e) => setForm({ ...form, geofence_radius_m: e.target.value })} /></div>
          </div>
          <Button data-testid="site-create-submit" className="w-full rounded-none mt-3" onClick={createSite}>Create site</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={postOpen} onOpenChange={setPostOpen}>
        <DialogContent className="rounded-none" data-testid="new-post-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add post — {sel?.name}</DialogTitle></DialogHeader>
          <Label className="text-xs uppercase tracking-wider font-bold">Post name</Label>
          <Input data-testid="post-name" className="rounded-none mt-1 mb-3" value={postForm.name} onChange={(e) => setPostForm({ ...postForm, name: e.target.value })} />
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">Required headcount</div>
          <div className="grid grid-cols-3 gap-3">
            {REQ_CATS.map(([k, label]) => (
              <div key={k} className="border border-border p-2">
                <div className="text-xs font-bold mb-2">{label}</div>
                <Label className="text-[10px] uppercase">Day</Label>
                <Input data-testid={`post-req-${k}-day`} type="number" min="0" className="rounded-none mb-2" value={postForm.requirements[k].day} onChange={(e) => setReq(k, "day", e.target.value)} />
                <Label className="text-[10px] uppercase">Night</Label>
                <Input data-testid={`post-req-${k}-night`} type="number" min="0" className="rounded-none" value={postForm.requirements[k].night} onChange={(e) => setReq(k, "night", e.target.value)} />
              </div>
            ))}
          </div>
          <Button data-testid="post-create-submit" className="w-full rounded-none mt-3" onClick={createPost}>Add post</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
